import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { MaxLengthTextField } from '../../components/FormFields/MaxLengthTextField';
import { NewProject, Project, ProjectStatus } from 'types/domainTypes';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { EditDialog } from 'components/EditDialog/EditDialog';
import { useAppSelector } from 'redux/hooks';
import { useGetUserStandardTemplateQuery } from 'redux/services/projects';

interface NewProjectDialogProps {
  title: string;
  onClose: () => void;
  onSaveProject: (project: NewProject) => void;
  error?: FetchBaseQueryError | SerializedError;
  project?: Project;
}

export type NewProjectFormData = {
  projectName: string;
  projectEngineer: string;
  projectClient: string;
  projectReference: string;
  projectDescription: string;
  projectNotes: string;
  projectStatus: ProjectStatus;
};

const newProjectFormDefaults: NewProjectFormData = {
  projectName: '',
  projectEngineer: '',
  projectClient: '',
  projectReference: '',
  projectDescription: '',
  projectNotes: '',
  projectStatus: ProjectStatus.InProgress,
};

const maxLengths: Record<keyof Omit<NewProjectFormData, 'projectStatus' | 'projectSupportWidth'>, number> = {
  projectName: 100,
  projectEngineer: 100,
  projectClient: 100,
  projectReference: 100,
  projectDescription: 500,
  projectNotes: 500,
};

export function NewProjectDialog({ title, onClose, onSaveProject, error, project }: NewProjectDialogProps): ReactElement {
  const { t } = useTranslation();
  const division = useAppSelector((state) => state.user.division);
  const userEmail = useAppSelector((state) => state.user.email);
  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty },
  } = useForm<NewProjectFormData>({ reValidateMode: 'onChange', defaultValues: newProjectFormDefaults });
  const { data: userStandard } = useGetUserStandardTemplateQuery(userEmail);

  const handleNewProjectDialogSave: SubmitHandler<NewProjectFormData> = (formData: NewProjectFormData) => {
    let standard = null;
    if (userStandard) {
      const tempUserStandard = JSON.parse(JSON.stringify(userStandard));
      delete tempUserStandard.user;
      standard = JSON.parse(JSON.stringify(tempUserStandard));
    }

    const newProject = {
      name: formData.projectName.trim(),
      engineer: formData.projectEngineer.trim(),
      client: formData.projectClient.trim(),
      description: formData.projectDescription.trim(),
      reference: formData.projectReference.trim(),
      notes: formData.projectNotes.trim(),
      status: formData.projectStatus,
      division: division,
      standard,
    };

    onSaveProject(newProject);
  };

  useEffect(() => {
    if (project !== undefined) {
      setValue('projectName', project.name);
      setValue('projectEngineer', project.engineer);
      setValue('projectClient', project.client || '');
      setValue('projectDescription', project.description || '');
      setValue('projectReference', project.reference || '');
      setValue('projectNotes', project.notes || '');
      setValue('projectStatus', project.status);
    }
  }, [project, setValue]);

  return (
    <EditDialog
      saveActionText={t('common.edit-dialog.action-save')}
      title={title}
      onClose={onClose}
      handleFormSubmit={handleSubmit(handleNewProjectDialogSave)}
      error={error}
      defaultErrorMessage={t('projects-page.project-dialog.error-message-save')}
      isDirty={isDirty}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <>
          {project && (
            <Controller
              name="projectStatus"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl component="fieldset" sx={{ paddingBottom: '16px' }}>
                  <FormLabel component="legend">{t('projects-page.project-dialog.label-project-status')}</FormLabel>
                  <RadioGroup {...field} aria-label="project-status" defaultValue={project.status} name="project-status-group">
                    <FormControlLabel
                      value={ProjectStatus.InProgress}
                      control={<Radio />}
                      label={t('projects-page.project-status-in-progress')}
                    />
                    <FormControlLabel
                      value={ProjectStatus.Completed}
                      control={<Radio />}
                      label={t('projects-page.project-status-completed')}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          )}
          <MaxLengthTextField
            name="projectName"
            control={control}
            required
            label={t('projects-page.project-dialog.label-name')}
            id="project-form-project-name"
            data-testid="project-form-project-name"
            fullWidth
            maxLength={maxLengths.projectName}
            autoFocus={!project} // Don't set focus when we're in edit mode
            multiline
          />
          <MaxLengthTextField
            name="projectEngineer"
            control={control}
            required
            label={t('projects-page.project-dialog.label-project-engineer')}
            id="project-form-project-engineer"
            data-testid="project-form-project-engineer"
            fullWidth
            maxLength={maxLengths.projectEngineer}
            multiline
          />
          <MaxLengthTextField
            name="projectClient"
            control={control}
            label={t('projects-page.project-dialog.label-client')}
            id="project-form-client"
            data-testid="project-form-client"
            fullWidth
            maxLength={maxLengths.projectClient}
            multiline
          />
          <MaxLengthTextField
            name="projectReference"
            control={control}
            label={t('projects-page.project-dialog.label-project-reference')}
            id="project-form-project-reference"
            data-testid="project-form-project-reference"
            fullWidth
            maxLength={maxLengths.projectReference}
            multiline
          />
          <MaxLengthTextField
            name="projectDescription"
            control={control}
            label={t('projects-page.project-dialog.label-project-description')}
            id="project-form-project-description"
            data-testid="project-form-project-description"
            fullWidth
            maxLength={maxLengths.projectDescription}
            multiline
          />
          <MaxLengthTextField
            name="projectNotes"
            control={control}
            label={t('projects-page.project-dialog.label-notes')}
            id="project-form-notes"
            data-testid="project-form-notes"
            fullWidth
            maxLength={maxLengths.projectNotes}
            multiline
          />
        </>
      </Stack>
    </EditDialog>
  );
}
